import { create } from 'zustand';
import { sessionManager, type SessionMetadata } from '../utils/SessionManager';
import { createLogger } from "../utils/logger";

const logger = createLogger("StorageStore");

// SDD 010: IndexedDB 단일화 — sessionManager가 모든 저장 담당

export interface SessionInfo {
  id: string;
  name: string;
  date: string;
  time: string;
  duration: string;
  startTime: Date;
  formats: string[];
  totalSize: string;
  avgFileSize: string;
  quality: number;
  deviceName: string;
  deviceId: string;
  size: string;
  notes: string;
}

export interface StorageStats {
  available: string;
  used: string;
  sessions: number;
  totalSize: string;
}

export interface StorageStore {
  isInitialized: boolean;
  storageReady: boolean;
  storageStats: StorageStats;
  sessions: SessionInfo[];
  currentSession: any | null;
  selectedSessions: string[];

  // 액션
  loadSessions: () => void;
  deleteSession: (sessionId: string) => Promise<void>;
  deleteSessions: (sessionIds: string[]) => Promise<void>;
  selectSession: (sessionId: string) => void;
  selectAllSessions: () => void;
  clearSelection: () => void;
  updateStorageStats: () => void;

  // SystemControlService에서 호출
  setCurrentSession: (sessionId: string | null) => void;
  setIsRecording: (isRecording: boolean) => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const metadataToSessionInfo = (m: SessionMetadata): SessionInfo => {
  const startTime = m.startTime ? new Date(m.startTime) : new Date();
  const totalSamples = (m.dataCount?.eeg || 0) + (m.dataCount?.ppg || 0) + (m.dataCount?.acc || 0);
  const estimatedSize = m.estimatedSize || ((m.dataCount?.eeg || 0) * 50 + (m.dataCount?.ppg || 0) * 30 + (m.dataCount?.acc || 0) * 40);

  return {
    id: m.id,
    name: m.name,
    date: startTime.toLocaleDateString(),
    time: startTime.toLocaleTimeString(),
    duration: formatDuration(m.duration || 0),
    startTime: startTime,
    formats: ['json', 'csv'],
    totalSize: formatBytes(estimatedSize),
    avgFileSize: formatBytes(totalSamples > 0 ? estimatedSize / 3 : 0),
    quality: 80,
    deviceName: m.deviceName || 'Unknown',
    deviceId: m.deviceId || 'unknown',
    size: formatBytes(estimatedSize),
    notes: m.notes || '',
  };
};

export const useStorageStore = create<StorageStore>((set, get) => ({
  isInitialized: true, // IndexedDB 항상 사용 가능
  storageReady: true,
  storageStats: { available: '2 GB', used: '0 B', sessions: 0, totalSize: '0 B' },
  sessions: [],
  currentSession: null,
  selectedSessions: [],

  loadSessions: () => {
    const metadata = sessionManager.getAllSessions();
    const sessionInfos = metadata
      .map(metadataToSessionInfo)
      .sort((a, b) => (b.startTime?.getTime?.() || 0) - (a.startTime?.getTime?.() || 0));

    const storageInfo = sessionManager.getStorageInfo();
    set({
      sessions: sessionInfos,
      storageStats: {
        available: '2 GB',
        used: formatBytes(storageInfo.used || 0),
        sessions: metadata.length,
        totalSize: formatBytes(storageInfo.used || 0),
      },
    });
  },

  deleteSession: async (sessionId: string) => {
    sessionManager.deleteSession(sessionId);
    get().loadSessions();
  },

  deleteSessions: async (sessionIds: string[]) => {
    for (const id of sessionIds) {
      sessionManager.deleteSession(id);
    }
    get().loadSessions();
  },

  selectSession: (sessionId: string) => {
    const { selectedSessions } = get();
    if (selectedSessions.includes(sessionId)) {
      set({ selectedSessions: selectedSessions.filter(id => id !== sessionId) });
    } else {
      set({ selectedSessions: [...selectedSessions, sessionId] });
    }
  },

  selectAllSessions: () => {
    set({ selectedSessions: get().sessions.map(s => s.id) });
  },

  clearSelection: () => {
    set({ selectedSessions: [] });
  },

  updateStorageStats: () => {
    get().loadSessions();
  },

  setCurrentSession: (sessionId: string | null) => {
    if (sessionId) {
      const m = sessionManager.getAllSessions().find(s => s.id === sessionId);
      set({ currentSession: m ? metadataToSessionInfo(m) : null });
    } else {
      set({ currentSession: null });
    }
  },

  setIsRecording: (_isRecording: boolean) => {
    // sessionManager가 자체 관리
  },

  // SDD 010: IndexedDB는 브라우저가 자동 관리 — no-op
  checkAndRestoreStorage: async () => true,
  initializeStorage: async () => {},
}));
